/**
 * The explicit, persisted device mode (ADR-UI-001 decision 5, WS-016).
 *
 * Kept separate from the async LayoutStore because it is a tiny synchronous
 * preference read on the critical path (it decides which presentation bundle to
 * load). Mirrors the shell's `ShellPreferenceCache` fail-soft pattern.
 */
import type { DeviceMode } from "./types";

export interface WorkspacePreferenceStore {
  readDeviceMode(): DeviceMode | null;
  writeDeviceMode(mode: DeviceMode): void;
}

const KEY = "thw.workspace.deviceMode";

function isDeviceMode(value: string | null): value is DeviceMode {
  return value === "desktop" || value === "tablet" || value === "mobile";
}

export const localStorageWorkspacePreferences: WorkspacePreferenceStore = {
  readDeviceMode() {
    try {
      const value = window.localStorage.getItem(KEY);
      return isDeviceMode(value) ? value : null;
    } catch {
      return null;
    }
  },
  writeDeviceMode(mode) {
    try {
      window.localStorage.setItem(KEY, mode);
    } catch {
      // Session remains usable without persistence.
    }
  },
};

export class InMemoryWorkspacePreferences implements WorkspacePreferenceStore {
  private mode: DeviceMode | null;

  constructor(initial?: DeviceMode) {
    this.mode = initial ?? null;
  }

  readDeviceMode(): DeviceMode | null {
    return this.mode;
  }

  writeDeviceMode(mode: DeviceMode): void {
    this.mode = mode;
  }
}
